import type {
    CatalogImportCandidate,
    CatalogImportLineResult,
    CatalogImportListItem,
    CatalogImportListPage,
    CatalogImportNameResult,
    CatalogImportNameSuggestion,
    CatalogImportRecord,
    CatalogImportResult,
    CatalogImportUndoResult,
} from '@/api/models';
import type { ErrorResponse } from '../../responses';
import { tetriFromDecimal } from '@/utils';
import type {
    CatalogImportCandidateWire,
    CatalogImportLineResultWire,
    CatalogImportLinesInvalidError,
    CatalogImportListItemWire,
    CatalogImportListPageWire,
    CatalogImportNameResultWire,
    CatalogImportRecordWire,
    CatalogImportResultWire,
    CatalogImportUndoWire,
} from './responses';

/**
 * Wire → domain for the catalog import. The only file that knows both.
 *
 * Money crosses as decimal GEL (JSON numbers). Every amount gains a `*Tetri`
 * twin here, through `tetriFromDecimal`, so the table, Result and history
 * format and add integers; the decimals stay as the wire's echo. Everything
 * else passes through, with the fields the server omits (a problem's empty
 * `fields`, a candidate's `parentName`, a suggestion's other fields) made
 * explicit, and what a server before API-195 lacks given its neutral value
 * (`totals.lines.draft` and `productsDrafted` 0, a suggestion's `reason`
 * `likelyTypo`).
 */

const tetriOfNullable = (value: number | null): number | null =>
    value === null ? null : tetriFromDecimal(value);

const toCandidate = (
    wire: CatalogImportCandidateWire,
): CatalogImportCandidate => ({
    ...wire,
    salePriceTetri: tetriOfNullable(wire.salePrice),
});

const toLine = (
    wire: CatalogImportLineResultWire,
): CatalogImportLineResult => ({
    ...wire,
    groupRows: wire.groupRows ?? [],
    optionValues: wire.optionValues ?? [],
    candidates: (wire.candidates ?? []).map(toCandidate),
    problems: wire.problems ?? [],
    warnings: wire.warnings ?? [],
});

/**
 * The three suggestion shapes, told apart by the field present (there is no
 * `kind`). `reason` passes through; a server before API-195 sends none, and
 * then suggested typos only.
 */
const toSuggestion = (
    wire: CatalogImportNameResultWire['suggestion'],
): CatalogImportNameSuggestion | null => {
    const reason = wire?.reason ?? 'likelyTypo';
    if (wire?.id) {
        return { id: wire.id, name: wire.name ?? '', reason };
    }
    if (wire?.text) {
        return { text: wire.text, reason };
    }
    if (wire?.rename) {
        return { rename: wire.rename, reason };
    }
    return null;
};

const toName = (
    wire: CatalogImportNameResultWire,
): CatalogImportNameResult => ({
    ...wire,
    axesToAdd: wire.axesToAdd ?? [],
    candidates: (wire.candidates ?? []).map((candidate) => ({
        id: candidate.id,
        name: candidate.name,
        parentName: candidate.parentName ?? null,
    })),
    suggestion: toSuggestion(wire.suggestion),
    problems: wire.problems ?? [],
});

export const toCatalogImportResult = (
    wire: CatalogImportResultWire,
): CatalogImportResult => ({
    lines: (wire.lines ?? []).map(toLine),
    names: {
        brands: (wire.names?.brands ?? []).map(toName),
        categories: (wire.names?.categories ?? []).map(toName),
        suppliers: (wire.names?.suppliers ?? []).map(toName),
    },
    totals: {
        ...wire.totals,
        lines: { ...wire.totals.lines, draft: wire.totals.lines.draft ?? 0 },
        netCostTetri: tetriFromDecimal(wire.totals.netCost),
        vatExcludedTetri: tetriFromDecimal(wire.totals.vatExcluded),
        saleValueTetri: tetriFromDecimal(wire.totals.saleValue),
    },
    header: {
        problems: wire.header?.problems ?? [],
        previousImport: wire.header?.previousImport ?? null,
    },
});

export const toCatalogImportRecord = (
    wire: CatalogImportRecordWire,
): CatalogImportRecord => ({
    ...wire,
    productsDrafted: wire.productsDrafted ?? 0,
    netCostTetri: tetriFromDecimal(wire.netCost),
    vatExcludedTetri: tetriFromDecimal(wire.vatExcluded),
    saleValueTetri: tetriFromDecimal(wire.saleValue),
});

export const toCatalogImportListItem = (
    wire: CatalogImportListItemWire,
): CatalogImportListItem => ({
    ...wire,
    productsDrafted: wire.productsDrafted ?? 0,
    netCostTetri: tetriFromDecimal(wire.netCost),
    saleValueTetri: tetriFromDecimal(wire.saleValue),
});

export const toCatalogImportListPage = (
    wire: CatalogImportListPageWire,
): CatalogImportListPage => ({
    items: (wire.items ?? []).map(toCatalogImportListItem),
    page: wire.page,
    pageSize: wire.pageSize,
    totalCount: wire.totalCount,
    totalPages: wire.totalPages,
});

export const toCatalogImportUndoResult = (
    wire: CatalogImportUndoWire,
): CatalogImportUndoResult => ({
    ...toCatalogImportRecord(wire),
    kept: wire.kept ?? [],
});

/**
 * The import's refusal with rows to fix, as opposed to any other: the one
 * whose `result` the caller shows in place of the table it submitted.
 */
export const isCatalogImportLinesInvalid = (
    error: ErrorResponse | null | undefined,
): error is CatalogImportLinesInvalidError =>
    error?.code === 'IMPORT_LINES_INVALID' &&
    'result' in error &&
    error.result !== undefined;
